import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { TrendingUp, Trophy, Users, FileText, MessageSquare, Star, Award, RotateCcw, Phone, MessageCircle, Mail, Copy, Megaphone } from "lucide-react";

const WINDOWS = [
    { value: 7, label: "Last 7 days" },
    { value: 30, label: "Last 30 days" },
    { value: 90, label: "Last 90 days" },
    { value: 365, label: "Last year" },
];

const COLS = [
    { key: "jobs_created",      label: "Jobs",       icon: FileText },
    { key: "candidates_added",  label: "Candidates", icon: Users },
    { key: "stage_changes",     label: "Stage moves", icon: RotateCcw },
    { key: "ratings_given",     label: "Ratings",    icon: Star },
    { key: "notes_added",       label: "Notes",      icon: FileText },
    { key: "messages_sent",     label: "Messages",   icon: MessageSquare },
    { key: "hires",             label: "Hires",      icon: Award },
];

export default function RecruiterPerformance() {
    const [days, setDays] = useState(30);
    const [data, setData] = useState(null);
    const [outbound, setOutbound] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                // Fetch both endpoints independently so a single failure
                // doesn't wipe the whole page. Team leaderboard on prod was
                // 500-ing when either aggregation hit an edge case; now the
                // page renders with whatever data survived.
                const [perfRes, obRes] = await Promise.all([
                    api.get("/recruiters/performance", { params: { days } })
                        .catch((e) => { toast.error(`Recruiter stats: ${formatApiError(e)}`); return { data: { window_days: days, recruiters: [] } }; }),
                    api.get("/dashboard/outbound-leaderboard", { params: { days } })
                        .catch(() => ({ data: { items: [] } })),
                ]);
                if (!cancelled) { setData(perfRes.data); setOutbound(obRes.data); }
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [days]);

    const recruiters = data?.recruiters || [];
    const maxActions = recruiters.reduce((m, r) => Math.max(m, r.total_actions || 0), 0) || 1;

    return (
        <div className="max-w-[1600px] mx-auto px-3 sm:px-4 py-3 sm:py-5 space-y-8">
            <div className="flex items-end justify-between gap-4 flex-wrap">
                <div>
                    <div className="text-xs uppercase tracking-widest font-mono text-ikb">Team performance</div>
                    <h1 className="font-display font-black text-2xl sm:text-4xl mt-2 flex items-center gap-3">
                        <Trophy className="w-8 h-8 text-amber-500" /> Recruiter activity
                    </h1>
                    <p className="text-muted-foreground mt-2">See who is moving candidates through the pipeline, sourcing the most, and closing hires.</p>
                </div>
                <div className="flex items-center gap-2">
                    <label className="text-xs font-mono uppercase tracking-widest text-muted-foreground">Window:</label>
                    <select
                        value={days}
                        onChange={(e) => setDays(parseInt(e.target.value, 10))}
                        className="h-9 px-2 border border-border bg-white text-sm"
                        data-testid="performance-window"
                    >
                        {WINDOWS.map((w) => <option key={w.value} value={w.value}>{w.label}</option>)}
                    </select>
                </div>
            </div>

            {loading ? (
                <div className="text-sm text-muted-foreground font-mono">Loading...</div>
            ) : recruiters.length === 0 ? (
                <div className="border border-dashed border-border p-16 text-center bg-white">
                    <TrendingUp className="w-8 h-8 mx-auto text-muted-foreground/50 mb-3" />
                    <h3 className="font-display font-bold text-xl">No activity yet in this window</h3>
                    <p className="text-sm text-muted-foreground mt-2">As recruiters update pipelines, add notes, and message candidates, you'll see live stats here.</p>
                </div>
            ) : (
                <div className="border border-border bg-white" data-testid="performance-table">
                    <div className="grid grid-cols-12 gap-2 px-5 py-3 border-b border-border bg-secondary/40 text-[10px] font-mono uppercase tracking-widest text-muted-foreground">
                        <div className="col-span-3">Recruiter</div>
                        <div className="col-span-2">Activity</div>
                        {COLS.map((c) => (
                            <div key={c.key} className="col-span-1 text-center hidden lg:block">{c.label}</div>
                        ))}
                    </div>
                    <div className="divide-y divide-border">
                        {recruiters.map((r, i) => {
                            const widthPct = Math.round((r.total_actions / maxActions) * 100);
                            return (
                                <div key={r.id} data-testid={`performance-row-${r.id}`} className="grid grid-cols-12 gap-2 px-5 py-4 items-center">
                                    <div className="col-span-12 lg:col-span-3 flex items-center gap-3">
                                        <div className="w-8 h-8 bg-foreground text-background flex items-center justify-center font-mono text-xs font-bold">
                                            {i === 0 ? <Trophy className="w-4 h-4 text-amber-400" /> : (r.name || "?").slice(0, 2).toUpperCase()}
                                        </div>
                                        <div className="min-w-0">
                                            <div className="font-medium text-sm truncate">{r.name}</div>
                                            <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground">{r.role}</div>
                                        </div>
                                    </div>
                                    <div className="col-span-12 lg:col-span-2">
                                        <div className="text-xs font-mono">{r.total_actions} actions</div>
                                        <div className="h-1.5 bg-secondary mt-1 relative">
                                            <div
                                                className="h-full bg-ikb absolute top-0 left-0"
                                                style={{ width: `${widthPct}%` }}
                                            />
                                        </div>
                                    </div>
                                    {COLS.map((c) => {
                                        const Icon = c.icon;
                                        return (
                                            <div key={c.key} className="col-span-3 sm:col-span-2 lg:col-span-1 text-center">
                                                <div className="text-xs font-mono text-muted-foreground lg:hidden inline-flex items-center gap-1 mr-2">
                                                    <Icon className="w-3 h-3" /> {c.label}:
                                                </div>
                                                <span className="text-sm font-bold tabular-nums" data-testid={`perf-${c.key}-${r.id}`}>
                                                    {r[c.key] ?? 0}
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {/* Outbound activity leaderboard (calls/whatsapp/email/copy) */}
            <div className="border border-border bg-white" data-testid="outbound-leaderboard">
                <div className="border-b border-border px-5 py-3 bg-secondary/40 flex items-center justify-between">
                    <h2 className="font-display font-bold text-sm uppercase tracking-widest flex items-center gap-2">
                        <Megaphone className="w-4 h-4 text-ikb" /> Outbound activity
                    </h2>
                    <div className="text-[10px] font-mono uppercase tracking-widest text-foreground/60">Last {days} days</div>
                </div>
                {(outbound?.items?.length || 0) === 0 ? (
                    <div className="p-8 text-center text-xs font-mono text-foreground/60">
                        No outbound activity logged in the last {days} days. Hits on Call / WhatsApp / Email / Copy from the candidate header are tracked here.
                    </div>
                ) : (
                    <table className="w-full text-sm">
                        <thead className="bg-secondary/30 text-[10px] font-mono uppercase tracking-widest text-foreground/70">
                            <tr>
                                <th className="text-left px-5 py-2">Recruiter</th>
                                <th className="px-3 py-2 text-right"><Phone className="w-3.5 h-3.5 inline mr-1" /> Calls</th>
                                <th className="px-3 py-2 text-right"><MessageCircle className="w-3.5 h-3.5 inline mr-1" /> WhatsApp</th>
                                <th className="px-3 py-2 text-right"><Mail className="w-3.5 h-3.5 inline mr-1" /> Emails</th>
                                <th className="px-3 py-2 text-right"><Copy className="w-3.5 h-3.5 inline mr-1" /> Copies</th>
                                <th className="px-5 py-2 text-right">Total</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {outbound.items.map((u, i) => (
                                <tr key={u.id} data-testid={`outbound-row-${u.id}`} className="hover:bg-secondary/30">
                                    <td className="px-5 py-3">
                                        <div className="flex items-center gap-2">
                                            <div className="w-7 h-7 bg-foreground text-background flex items-center justify-center font-mono text-xs font-bold">
                                                {i === 0 ? <Trophy className="w-3.5 h-3.5 text-amber-400" /> : (u.name || "?").slice(0, 2).toUpperCase()}
                                            </div>
                                            <div className="min-w-0">
                                                <div className="font-medium text-sm truncate">{u.name || u.email}</div>
                                                <div className="text-[10px] uppercase tracking-widest font-mono text-muted-foreground">{u.role}</div>
                                            </div>
                                        </div>
                                    </td>
                                    <td className="px-3 py-3 text-right font-mono text-sm" data-testid={`ob-call-${u.id}`}>{u.call || 0}</td>
                                    <td className="px-3 py-3 text-right font-mono text-sm" data-testid={`ob-wa-${u.id}`}>{u.whatsapp || 0}</td>
                                    <td className="px-3 py-3 text-right font-mono text-sm" data-testid={`ob-email-${u.id}`}>{u.email || 0}</td>
                                    <td className="px-3 py-3 text-right font-mono text-sm" data-testid={`ob-copy-${u.id}`}>{u.copy || 0}</td>
                                    <td className="px-5 py-3 text-right font-bold tabular-nums" data-testid={`ob-total-${u.id}`}>{u.total || 0}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}
